import type { NoteEntry } from '@ni/shared';
import type { NoteLine, NotePiece } from '../components/record/notes-list';
import {
  NOTE_CONFIDENTIAL,
  NOTE_NOT_READ,
  NOTE_READING,
  noteEditedBy,
  pastedInPieces,
  pastedReadingState,
} from './copy';
import { formatDate, formatDay, joinClauses } from './format';

/**
 * **The Notes rail's reading of the record's own prose**, as a pure function of
 * the payload.
 *
 * It was six lines inside `overview.tsx` until a pasted note stopped being one
 * row (`docs/notes/2026-09-14-split-giant-intake-note.md`). A note over the
 * splitter's threshold is stored as several `prose_input` rows, each with its
 * own reading, and the rail has to show the paste the office made rather than
 * the rows the server wrote: **one entry, collapsed**, with a header that says
 * how many pieces there are and how far the reading has got across them.
 *
 * Out here rather than in the component for the reason the component's own
 * docblock gives — the caller does the reading, `NotesList` does the rendering —
 * and out of the route so that the grouping is asserted as data, on fixtures
 * that go through the wire schema, instead of by reading it out of markup.
 *
 * **Consecutive rows, not a scan of the whole list.** The server orders a
 * record's prose newest first and, within one transaction's tie, by
 * `piece_seq` — so a paste's pieces arrive adjacent and in reading order, and
 * grouping the run in front of us is the same answer as indexing the list with
 * none of the reordering that would silently repair a payload that had stopped
 * being ordered. If the order ever breaks, this breaks visibly with it.
 */
export function noteRailLines(notes: readonly NoteEntry[]): NoteLine[] {
  const lines: NoteLine[] = [];

  for (let index = 0; index < notes.length; index += 1) {
    const note = notes[index]!;
    if (note.split_group_id === null) {
      lines.push(plainLine(note));
      continue;
    }

    let end = index + 1;
    while (end < notes.length && notes[end]!.split_group_id === note.split_group_id) end += 1;
    lines.push(groupLine(notes.slice(index, end)));
    index = end - 1;
  }

  return lines;
}

/** A note the office typed, on the line it has always been on. */
function plainLine(note: NoteEntry): NoteLine {
  return {
    key: note.input_id,
    inputId: note.input_id,
    date: dateOf(note),
    prose: note.prose,
    note: joinClauses([
      note.author.name ?? 'office',
      note.edited ? noteEditedBy(note.edited.by.name) : null,
      note.content_date_basis === 'inferred' ? 'date read from the note' : null,
      /* Said where the office would look for it: under the words, beside who
         wrote them. A confidential note has no reading state to add. */
      note.confidential ? NOTE_CONFIDENTIAL : null,
      /* The rail says what the record's own banner says, in the rail's quieter
         words: a note being read now is not the same absence as a note nothing
         will read again. */
      stateClause(note),
    ]),
  };
}

/**
 * **One paste, folded.** The header is the day, whoever pasted it, how many
 * pieces it became and how far the reading has got; the words are under it, in
 * sequence, each saying what is true of its own reading.
 *
 * The date and the author come off the first piece, because they are facts about
 * the paste and not about the row: every piece of one paste is written in one
 * transaction by one person with one content date.
 */
function groupLine(pieces: readonly NoteEntry[]): NoteLine {
  const first = pieces[0]!;
  const counts = {
    pieces: pieces.length,
    read: pieces.filter((piece) => piece.extraction_status === 'done').length,
    reading: pieces.filter((piece) => piece.extraction_status === 'pending').length,
    failed: pieces.filter((piece) => piece.extraction_status === 'failed').length,
  };

  return {
    key: first.split_group_id ?? first.input_id,
    inputId: first.input_id,
    date: dateOf(first),
    /* Unused on a group: the summary is a header, and the words are in the
       pieces under it. */
    prose: '',
    note: joinClauses([
      first.author.name ?? 'office',
      first.edited ? noteEditedBy(first.edited.by.name) : null,
      /* One paste, one mark: every piece was written in one transaction with
         one instruction, so the first piece speaks for all of them. */
      first.confidential ? NOTE_CONFIDENTIAL : null,
      pastedInPieces(counts.pieces),
      /* A confidential paste has no readings to count. */
      first.confidential ? null : pastedReadingState(counts),
    ]),
    pieces: pieces.map((piece): NotePiece => ({
      key: piece.input_id,
      prose: piece.prose,
      note: stateClause(piece),
    })),
  };
}

/** The day the rail reckons a note by: what the note says, or when it was filed. */
function dateOf(note: NoteEntry): string {
  return formatDate(note.content_date) || formatDay(note.created_at);
}

/** What is true of one row's reading, or nothing where there is nothing to say. */
function stateClause(note: NoteEntry): string | null {
  if (note.extraction_status === 'pending') return NOTE_READING;
  if (note.extraction_status === 'failed') return NOTE_NOT_READ;
  return null;
}
